import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Page } from '@bloomreach/spa-sdk';
import { InsightsService } from '@marketing/insights/insights.service';
import {
  FtInsightsItem,
  RelatedArticleResults,
} from '@search/interfaces/insights.interface';
import {
  AppStateService,
  ProfileService,
  SiteConfigService,
  WidenService,
} from '@services';
import { LinkService } from '@services/link.service';
import { Link } from '@types';
import { HEADER_LOGO } from '@utils/app.constants';
import { convertCsvFields } from '@utils/data/object-utils';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ArticleContent,
  ArticleProfileSetting,
  Contributor,
  ProfileContentType,
} from '../article.types';

/**
 * Logger
 */
const logger = Logger.getLogger('ArticleService');

/**
 * The Article Service contains some of the complex business logic that is not required
 * in the component.
 */
@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  private unsubscribe$: Subject<void> = new Subject<void>();

  // TODO: Move complex business logic out of article component and into this service
  constructor(
    private appStateService: AppStateService,
    private ctaLinkService: LinkService,
    private meta: Meta,
    private pageTitle: Title,
    private profileService: ProfileService,
    private siteConfig: SiteConfigService,
    private widenService: WidenService,
    private insightsService: InsightsService
  ) {}

  audienceWidenAsset(audience: string): boolean {
    if (audience !== undefined) {
      const audienceList = audience.split(',');
      const segmentId = this.siteConfig.segmentId;
      for (const audi of audienceList) {
        if (audi === segmentId) {
          return true;
        } else {
          return audi === 'advisor' && segmentId === 'financial-professionals';
        }
      }
    }
  }

  /**
   * Get Article Date from document object
   */
  getArticleDate(documentRef: string, page: Page): string {
    const relatedArticle = page
      .getContent(documentRef)
      ?.getData<ArticleContent>();
    return relatedArticle?.isodate;
  }

  // Set meta tags
  setMetaTags(metadata: any, imageUrl: string, title: string): void {
    if (metadata) {
      if (!metadata.title) {
        this.pageTitle.setTitle(
          title +
            (metadata.appendedTitleText
              ? ' | ' + metadata.appendedTitleText
              : '')
        );
      }
      if (!metadata.socialImage) {
        this.meta.updateTag({
          property: 'og:image',
          content: imageUrl,
        });
      }
      this.meta.updateTag({
        property: 'twitter:card',
        content: 'summary_large_image',
      });
      this.meta.addTags([
        {
          property: 'twitter:title',
          content: metadata.title,
        },
        {
          property: 'twitter:description',
          content: metadata.description,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        },
      ]);
    }
  }

  getSocialMediaImg(article: ArticleContent): string {
    let imageUrl = HEADER_LOGO;
    if (article?.image?.widenDocument) {
      imageUrl = this.widenService.getWidenImageVariantUrl(
        this.widenService.getWidenAssetUrl(article.image?.widenDocument),
        'original',
        'jpeg'
      );
    }
    return imageUrl;
  }

  /**
   * Formats the authors of an article based on the provided article content and page.
   * @param article - The article content.
   * @param page - The page containing the article.
   */
  formatAuthors(article: ArticleContent, page: Page): void {
    article.authors = [];
    for (const contributor of article?.insightMetadata?.contributors) {
      const contributorData = page
        .getContent(contributor)
        .getData<Contributor>();

      if (contributorData?.fullName) {
        const authorName = contributorData?.fullName;
        article.authors?.push(authorName);
      }
    }
  }

  /**
   * Formats the contributors of an article.
   * updates tags and profile image urls
   * @param article - The article content.
   * @param page - The page containing the article.
   */
  formatContributors(
    article: ArticleContent,
    page: Page,
    articleProfileSetting?: ArticleProfileSetting
  ): void {
    const contributors: Contributor[] = [];
    const contributorReferences =
      article?.insightMetadata?.contributors || article?.contributors;
    for (const contributor of contributorReferences) {
      const contributorData: Contributor = page
        .getContent(contributor)
        .getData<Contributor>();

      this.createProfileUrl(contributorData);
      // Adding p tag if p tag does not exist on API
      if (contributorData?.overview) {
        contributorData.overview = this.addPTag(contributorData?.overview);
      }
      this.createProfileLink(contributorData, articleProfileSetting);

      if (contributorData?.contentType === ProfileContentType.FTBLOG_PROFILE) {
        this.createFullname(contributorData);
        // Adding p tag to role and employer if required
        if (contributorData?.role) {
          contributorData.overview = this.addPTag(contributorData?.role);
        }

        if (contributorData?.employer) {
          contributorData.text2 = this.addPTag(contributorData?.employer);
        }
      }
      contributors.push(contributorData);
    }

    article.contributors = contributors;
  }

  /**
   * Creates a profile URL for the given contributor data.
   * If the contributor has a profile image, it generates a URL for the image using the Widen service.
   * If the contributor does not have a profile image or the URL is undefined, it sets a default image URL.
   * @param contributorData The contributor data object.
   */
  private createProfileUrl(contributorData: Contributor): void {
    if (contributorData?.profileImage) {
      const profileImage =
        contributorData.profileImage?.widenAsset ||
        contributorData.profileImage?.widenDocument;

      contributorData.profileImageUrl = this.widenService.getWidenImageVariantUrl(
        this.widenService.getWidenAssetUrl(profileImage),
        'original',
        'webp'
      );
    }
    if (
      !contributorData?.profileImageUrl ||
      contributorData?.profileImage === 'undefined'
    ) {
      contributorData.profileImageUrl = '/assets/images/blank-spacer.png';
    }
  }

  /**
   * Creates a profile LINK for the given contributor data.
   * If the  Profile is Core and and contributor has sharedUrl, it generates href for the profile link.
   * If the Profile is Blog,contributor has relativePath and it has ArticleProfileSetting( useProfileLink,
   * profileBasePath,profileLinkRelativePath) , it generated href for the profile link by  the combination
   * relativePath,profileBasePath and profileLinkRelativePath.
   * @param contributorData The contributor data object.
   * @param articleProfileSetting The ArticleProfileSetting data object.
   */
  private createProfileLink(
    contributorData: Contributor,
    articleProfileSetting?: ArticleProfileSetting
  ): void {
    if (contributorData?.contentType === ProfileContentType.FTCORE_PROFILE) {
      contributorData.href =
        articleProfileSetting !== undefined && contributorData?.sharedUrl
          ? contributorData?.sharedUrl
          : '';
    }

    if (
      contributorData?.contentType === ProfileContentType.FTBLOG_PROFILE &&
      articleProfileSetting?.useProfileLink &&
      articleProfileSetting?.profileBasePath &&
      articleProfileSetting?.profileLinkRelativePath &&
      contributorData?.relativePath
    ) {
      contributorData.href =
        this.appStateService?.getSpaBaseUrl() +
        contributorData?.relativePath?.replace(
          articleProfileSetting?.profileLinkRelativePath,
          articleProfileSetting?.profileBasePath
        );
    }
  }

  /**
   * Creates a full name for the contributor if it is not already present.
   * The full name is constructed by combining the display name and qualifications (if available).
   * @param contributorData - The contributor data object.
   */
  private createFullname(contributorData: Contributor): void {
    if (!contributorData.fullName && contributorData?.displayName) {
      contributorData.fullName =
        contributorData?.displayName +
        (contributorData?.qualifications
          ? ',' + contributorData?.qualifications
          : '');
    }
  }

  /**
   * Adds a <p> tag to the given string if it doesn't already start with <p>.
   * @param data - The string to add the <p> tag to.
   * @returns The modified string with the <p> tag.
   */
  public addPTag(data: string): string {
    if (data && !data?.startsWith('<p>')) {
      return `<p>${data}</p>`;
    }
    return data;
  }

  /**
   * Format the metadata
   * @param article R
   */
  formatMetadata(article: ArticleContent): void {
    if (article?.insightMetadata?.investmentteam) {
      article.investmentteam = article?.insightMetadata?.investmentteam;
    }

    if (article?.insightMetadata?.investmenttheme) {
      article.investmenttheme = article?.insightMetadata?.investmenttheme;
    }

    if (article?.insightMetadata?.assetclass) {
      article.assetclassPage = article?.insightMetadata?.assetclass;
    }

    if (article?.insightMetadata?.speciality) {
      article.speciality = article?.insightMetadata?.speciality;
    }

    if (article?.insightMetadata?.publicationseries) {
      article.publicationseries = article?.insightMetadata?.publicationseries;
    }

    if (article?.insightMetadata?.audienceExclusions) {
      article.audienceExclusions = article?.insightMetadata?.audienceExclusions;
    }
  }

  // Related Content
  getRelatedContent(
    segmentTags: string,
    article: ArticleContent,
    page: Page
  ): void {
    for (const relatedContent of article.relatedContent) {
      if (relatedContent.localizableLink.length) {
        for (const localizeLink of relatedContent.localizableLink) {
          localizeLink.isAllowed = true;
          if (localizeLink.targeting) {
            localizeLink.isAllowed = this.isSegmentTagAvailable(
              segmentTags,
              localizeLink.targeting
            );
          }
          if (localizeLink?.link?.linkCollection?.length) {
            const linkBlock: Link = localizeLink?.link?.linkCollection[0];
            localizeLink.link.displayText = linkBlock.displayText;
            localizeLink.link.target = linkBlock.target;
            localizeLink.link.external = linkBlock.external;
            localizeLink.link.externalSuffix = linkBlock.externalSuffix;
            localizeLink.link.title = linkBlock.title;
            localizeLink.link.url = this.ctaLinkService.getCTALink(
              page,
              linkBlock,
              this.appStateService?.getHomePageUrl()
            );
            if (linkBlock?.document) {
              logger.debug('This is a document link');
              localizeLink.link.date = this.getArticleDate(
                linkBlock.document,
                page
              );
            }
            if (linkBlock?.literatureCode) {
              const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
              localizeLink.link.url =
                spaBaseURl +
                this.siteConfig.literature.litDetailUrl +
                '/' +
                linkBlock.literatureCode;
            }
          }
          if (localizeLink.isodate) {
            localizeLink.isodate = this.insightsService.getLongDate(
              localizeLink.isodate
            );
          }
        }
      }
      if (relatedContent.dynamicFilters) {
        this.insightsService
          .getAricleAPIData$(
            relatedContent.dynamicFilters?.articleFilters,
            relatedContent.dynamicFilters?.maxResults
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((response: any) => {
            const results = response.results.response.hits.hits;
            results.forEach((element: FtInsightsItem) => {
              let articleUrl =
                this.appStateService?.getSpaBaseUrl() +
                '/' +
                element._source?.relativePath;
              if (element._source?.relativePath?.startsWith('/')) {
                articleUrl =
                  this.appStateService?.getSpaBaseUrl() +
                  element._source?.relativePath;
              }
              relatedContent.localizableLink.push({
                isAllowed: true,
                isodate: this.insightsService.getLongDate(
                  element._source?.referenceDate
                ),
                link: {
                  displayText: this.replaceSpecialCharacters(
                    element._source?.title
                  ),
                  target: '',
                  external: false,
                  title: element._source?.title,
                  url: articleUrl,
                },
              });
            });
            relatedContent.localizableLink.forEach((element) => {
              if (element.isAllowed) {
                relatedContent.showHeading = true;
              }
            });
          });
      }
      relatedContent.localizableLink.forEach((element) => {
        if (element.isAllowed) {
          relatedContent.showHeading = true;
        }
      });
    }
  }

  public getRelatedArticleContent$(
    currentArticleUrl: string,
    maxLimit: number
  ): Observable<RelatedArticleResults> {
    return this.insightsService.getRelatedArticleData$(
      currentArticleUrl,
      maxLimit
    );
  }

  /**
   * Replaces HTML special character codes
   * @param input segment tag stringraw string
   * @returns formatted string
   */
  replaceSpecialCharacters(input: string): string {
    const specialChars: {
      htmlChars: string;
      replacedValue: string;
    }[] = [
      {
        htmlChars: '&rsquo;',
        replacedValue: '’',
      },
    ];

    for (const specialChar of specialChars) {
      const regex = new RegExp(specialChar.htmlChars, 'g');
      input = input.replace(regex, specialChar.replacedValue);
    }

    return input;
  }

  /**
   * Set Local content (Moved from component)
   * @param segmentTags segment tag string
   * @param article articleContent object
   */
  public setLocalizableContent(
    segmentTags: string,
    article: ArticleContent
  ): void {
    article.filterContent = [];
    article.contentBlocks.forEach((localizeContent) => {
      localizeContent.isAllowed = true;
      if (localizeContent.targeting) {
        localizeContent.isAllowed = this.isSegmentTagAvailable(
          segmentTags,
          localizeContent.targeting
        );
      }

      if (localizeContent.isAllowed) {
        article.filterContent.push(localizeContent);
      }
    });
  }

  /**
   * Set Local pdf (Moved from component)
   * @param segmentTags segment tag string
   * @param article articleContent object
   */
  setLocalizablePdf(segmentTags: string, article: ArticleContent): void {
    article.pdf = [];
    article.localizablePdf.forEach((localizePdf) => {
      localizePdf.isAllowed = true;
      if (localizePdf.targeting) {
        localizePdf.isAllowed = this.isSegmentTagAvailable(
          segmentTags,
          localizePdf.targeting
        );
      }

      if (localizePdf.pdf.target === '_blank') {
        localizePdf.pdf.isNewWindow = true;
      }

      const widenAsset = localizePdf.pdf.widenAssetCompound.widenDocument;
      const litCode = this.widenService.getLitCode(widenAsset);
      const isClientUse = this.widenService.getIsClientUse(widenAsset);
      const audience = this.audienceWidenAsset(
        this.widenService.getWidenAssetAudience(widenAsset)
      );

      localizePdf.pdf.href = this.widenService.getWidenAssetUrl(widenAsset);

      if (
        audience &&
        !this.profileService.isLoggedIn() &&
        litCode !== undefined
      ) {
        const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
        localizePdf.pdf.href =
          spaBaseURl + this.siteConfig.literature.litDetailUrl + '/' + litCode;
      }

      if (localizePdf.isAllowed) {
        article.pdf.push(localizePdf.pdf);
      }
    });
  }

  public setTargetingLinkCompound(
    segmentTags: string,
    article: ArticleContent,
    page: Page
  ): void {
    if (article?.targetingLinkCompound?.length) {
      article.pdf = [];
      article.targetingLinkCompound.forEach((targetedPdf) => {
        const linkCollectionData = targetedPdf?.link?.linkCollection[0];
        targetedPdf.isAllowed = true;
        if (targetedPdf?.targeting) {
          targetedPdf.isAllowed = this.isSegmentTagAvailable(
            segmentTags,
            targetedPdf.targeting
          );
        }

        if (linkCollectionData?.target === '_blank') {
          targetedPdf.isNewWindow = true;
        }

        if (targetedPdf?.link?.linkCollection?.length) {
          const linkBlock: Link = targetedPdf?.link?.linkCollection[0];

          targetedPdf.iconType = linkBlock?.iconType;
          targetedPdf.link.displayText = linkBlock?.displayText;
          targetedPdf.link.target = linkBlock?.target;
          targetedPdf.link.external = linkBlock?.external;
          targetedPdf.link.title = linkBlock?.title;
          targetedPdf.link.signInRequired = linkBlock?.signInLink;

          if (linkBlock?.document?.$ref) {
            const internalLink = page.getContent(linkBlock?.document?.$ref);
            targetedPdf.link.url = internalLink?.getUrl();
          }
          if (linkBlock?.document) {
            logger.debug('This is a document link');
            targetedPdf.link.date = this.getArticleDate(
              linkBlock.document,
              page
            );
          }
          if (linkBlock?.literatureCode) {
            const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
            targetedPdf.link.url =
              spaBaseURl +
              this.siteConfig.literature.litDetailUrl +
              '/' +
              linkBlock.literatureCode;
          }
          if (linkBlock?.widenAssetCompound) {
            const widenAsset = linkBlock?.widenAssetCompound?.widenDocument;
            const litCode = this.widenService.getLitCode(widenAsset);
            const isClientUse = this.widenService.getIsClientUse(widenAsset);
            const audience = this.audienceWidenAsset(
              this.widenService.getWidenAssetAudience(widenAsset)
            );
            targetedPdf.link.url = this.widenService.getWidenAssetUrl(
              widenAsset
            );

            if (
              audience &&
              !this.profileService.isLoggedIn() &&
              litCode !== undefined
            ) {
              const spaBaseURl = this.appStateService?.getCmsBaseUrl() + '/';
              targetedPdf.link.url =
                spaBaseURl +
                this.siteConfig.literature.litDetailUrl +
                '/' +
                litCode;
            }
          }
          if (linkBlock?.url && !targetedPdf?.link?.url) {
            targetedPdf.link.url = linkBlock.url;
          }
        }
      });
    }
  }

  /**
   * Check if tags are matching with site config.
   * (Moved from component)
   */
  isSegmentTagAvailable(segmentTags: string, target: string): boolean {
    if (target) {
      // parsedValue can be an Array or an Object sometimes
      const parsedValue: any = JSON.parse(target);
      const documentAudience: any = parsedValue?.insightAudiences_csv
        ? convertCsvFields(parsedValue?.insightAudiences_csv)
        : parsedValue;
      const siteAudience = segmentTags?.split(',');
      const includeAudience = siteAudience?.filter((audience) =>
        documentAudience?.includes(audience)
      );
      return !!includeAudience?.length;
    }
  }

  /**
   * This method extracts the page reference URL
   * @param page Page reference
   * @param documentRef string
   * @returns string which contains the URL
   */
  public getCardLink(page: Page, documentRef: string): string {
    const internalLink = page?.getContent(documentRef)?.getUrl();

    return internalLink || '';
  }
}
